import React from 'react';
import { motion } from 'framer-motion';
import { FileSignature, Shield, Users, Zap, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';

const WelcomeStep = ({ onNext }) => {
  const { user, completeOnboardingStep } = useAuth();
  const { completeStep } = useOnboarding();
  const hasCompletedRef = React.useRef(false);

  // Auto-complete the welcome step since it's just informational
  React.useEffect(() => {
    // Prevent multiple executions
    if (hasCompletedRef.current) {
      console.log('🔄 WelcomeStep: Already completed, skipping...');
      return;
    }

    const markWelcomeComplete = async () => {
      try {
        console.log('🚀 WelcomeStep: Auto-completing welcome step...');
        hasCompletedRef.current = true; // Mark as in progress
        
        // Complete both locally and on backend
        await completeOnboardingStep('welcome');
        completeStep('welcome');
        console.log('✅ WelcomeStep: Welcome step completed successfully');
      } catch (error) {
        console.error('❌ WelcomeStep: Error completing welcome step:', error);
        // Reset flag on error so it can be retried
        hasCompletedRef.current = false;
        // Still complete locally even if API fails
        completeStep('welcome');
      }
    };
    
    markWelcomeComplete();
  }, []); // Empty dependency array to run only once

  const features = [
    {
      icon: <FileSignature className="w-6 h-6 text-blue-500" />,
      title: "Digital Signatures",
      description: "Create legally binding signatures with advanced security"
    },
    {
      icon: <Shield className="w-6 h-6 text-green-500" />,
      title: "Contract Analysis",
      description: "AI-powered contract review and risk assessment"
    },
    {
      icon: <Users className="w-6 h-6 text-purple-500" />,
      title: "Team Collaboration",
      description: "Share documents and manage permissions effortlessly"
    },
    {
      icon: <Zap className="w-6 h-6 text-yellow-500" />,
      title: "Fast & Secure",
      description: "Industry-leading speed with bank-grade security"
    }
  ];


  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to SignPact, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We're excited to help you streamline your document signing and contract management workflow. 
            Let's get you set up with everything you need to get started.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
            className="bg-gray-50 rounded-xl p-6 border border-gray-100"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="bg-blue-50 rounded-xl p-6 border border-blue-100"
      >
        <div className="flex items-start space-x-4">
          <CheckCircle className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              What to expect
            </h3>
            <p className="text-blue-800 mb-4">
              This quick setup will help us personalize your experience and ensure you get the most out of SignPact. 
              We'll walk you through:
            </p>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Setting up your profile and preferences</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Customizing your notification settings</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>A quick tour of key features</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>

    </div>
  );
};

export default WelcomeStep;